import { caseAPI } from './api';
import { config } from '../config/environment';

export interface Case {
  id: number;
  title: string;
  subtitle?: string;
  description?: string;
  project_id: number;
  project?: {
    id: number;
    name: string;
    firm_id?: number;
  };
  creator_id: number;
  creator?: {
    id: number;
    name: string;
  };
  assigned_to?: {
    id: number;
    name: string;
  };
  assigned_vendor_id?: number;
  assigned_vendor?: {
    id: number;
    name: string;
  };
  parent_case_id?: number;
  status: string;
  priority?: string;
  type?: string;
  free_case?: boolean;
  estimate?: {
    estimate_hours?: number;
    finalize_estimate?: boolean;
  };
  file_number?: string;
  created_at: string;
  updated_at: string;
}

export interface CaseListResponse {
  success: boolean;
  data: {
    data: Case[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const caseService = {
  // Get all cases
  getAllCases: async (params?: any): Promise<CaseListResponse> => {
    try {
      const response = await caseAPI.getAll(params);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch cases');
    }
  },

  // Get cases for a specific firm
  getFirmCases: async (firmId: number, params?: any): Promise<CaseListResponse> => {
    try {
      const response = await caseAPI.getAll({ ...params, firm_id: firmId });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch firm cases');
    }
  },

  // Get case by ID
  getCaseById: async (id: number): Promise<{ success: boolean; data: Case }> => {
    try {
      const response = await caseAPI.getById(id);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch case');
    }
  },

  getMeta: async (): Promise<{ success: boolean; data: any }> => {
    const response = await caseAPI.getMeta();
    return response.data;
  },

  // Create new case
  createCase: async (data: any): Promise<any> => {
    try {
      let payload = data;
      if (data && Array.isArray(data.attachments)) {
        const files = (data.attachments as any[]).filter((f) => typeof File !== 'undefined' && f instanceof File);
        // If no real files, drop attachments entirely
        if (files.length === 0) {
          const { attachments, ...rest } = data;
          payload = rest;
        } else {
        const fd = new FormData();
        Object.keys(data).forEach((k) => {
          if (k === 'attachments') return;
          const val = (data as any)[k];
          if (Array.isArray(val)) {
            val.forEach((v) => fd.append(`${k}[]`, v));
          } else if (val !== undefined && val !== null) {
            fd.append(k, val as any);
          }
        });
        files.forEach((file: File) => fd.append('attachments[]', file));
        payload = fd;
        }
      }
      const response = await caseAPI.create(payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create case');
    }
  },

  // Update case
  updateCase: async (id: number, data: any): Promise<any> => {
    try {
      let payload = data;
      if (data && data.attachments && Array.isArray(data.attachments)) {
        const fd = new FormData();
        Object.keys(data).forEach((k) => {
          if (k === 'attachments') return;
          const val = (data as any)[k];
          if (Array.isArray(val)) {
            val.forEach((v) => fd.append(`${k}[]`, v));
          } else if (val !== undefined && val !== null) {
            fd.append(k, val as any);
          }
        });
        (data.attachments as File[]).forEach((file: File) => fd.append('attachments[]', file));
        payload = fd;
      }
      const response = await caseAPI.update(id, payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update case');
    }
  },

  // Delete case
  deleteCase: async (id: number): Promise<any> => {
    try {
      const response = await caseAPI.delete(id);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to delete case');
    }
  },

  // Reopen a delivered case
  reopenCase: async (caseId: number, options?: { skip_vendor_reopen?: boolean }): Promise<any> => {
    try {
      const res = await caseAPI.reopen(caseId, options);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to reopen case');
    }
  },

  // Assign vendor to case (supports vendor_user_id and due_date)
  assignVendor: async (caseId: number, payload: { vendor_id: number; vendor_user_id?: number; due_date?: string }): Promise<any> => {
    try {
      const response = await caseAPI.assignVendor(caseId, payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to assign vendor');
    }
  },

  // Reassign vendor to case
  reassignVendor: async (caseId: number, payload: { vendor_id: number; vendor_user_id?: number; due_date?: string; reassignment_reason: string }): Promise<any> => {
    try {
      const response = await caseAPI.reassignVendor(caseId, payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to reassign vendor');
    }
  },

  // Get vendor assignment history
  getVendorAssignmentHistory: async (caseId: number): Promise<any> => {
    try {
      const response = await caseAPI.getVendorAssignmentHistory(caseId);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch assignment history');
    }
  },

  // Upload file to case
  uploadFile: async (caseId: number, file: File, formData?: FormData, onProgress?: (progress: number) => void): Promise<any> => {
    try {
      const response = await caseAPI.uploadFile(caseId, file, formData, onProgress);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to upload file');
    }
  },

  // Upload multiple files to case
  uploadMultipleFiles: async (caseId: number, files: File[], options?: {
    isMrrDelivery?: boolean;
    isCaseDelivery?: boolean;
    deliveryComment?: string;
    tag?: string;
    isAdditionalUpload?: boolean;
  }, onProgress?: (fileName: string, progress: number) => void): Promise<any> => {
    try {
      const promises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        if (options?.isMrrDelivery) {
          formData.append('is_mrr_delivery', '1');
        } else if (options?.isCaseDelivery) {
          formData.append('is_case_delivery', '1');
          if (options.deliveryComment) {
            formData.append('delivery_comment', options.deliveryComment);
          }
        }
        
        if (options?.tag) {
          formData.append('tag', options.tag);
        }
        if (options?.isAdditionalUpload) {
          formData.append('is_additional_upload', '1');
        }
        
        const progressCallback = onProgress ? (progress: number) => onProgress(file.name, progress) : undefined;
        return caseAPI.uploadFile(caseId, file, formData, progressCallback);
      });
      
      const responses = await Promise.all(promises);
      return responses.map(response => response.data);
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to upload files');
    }
  },

  // Upload ZIP file to case
  uploadZipFile: async (caseId: number, file: File, options?: {
    isMrrDelivery?: boolean;
    isCaseDelivery?: boolean;
    deliveryComment?: string;
    tag?: string;
    isAdditionalUpload?: boolean;
  }, onProgress?: (progress: number) => void): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (options?.isMrrDelivery) {
        formData.append('is_mrr_delivery', '1');
      } else if (options?.isCaseDelivery) {
        formData.append('is_case_delivery', '1');
        if (options.deliveryComment) {
          formData.append('delivery_comment', options.deliveryComment);
        }
      }
      
      if (options?.tag) {
        formData.append('tag', options.tag);
      }
      if (options?.isAdditionalUpload) {
        formData.append('is_additional_upload', '1');
      }
      
      const response = await caseAPI.uploadZip(caseId, file, formData, onProgress);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to upload ZIP file');
    }
  },

  // Timer APIs
  listTimeEntries: async (caseId: number): Promise<{
    success: boolean;
    data: {
      entries: any[];
      total_seconds: number;
      running_session?: { session_id: number; started_at: number } | null;
      pending_session?: { session_id: number; started_at: number; stopped_at?: number | null } | null;
    };
  }> => {
    try {
      const res = await caseAPI.listTimeEntries(caseId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch time entries');
    }
  },
  getActiveTimers: async (caseId: number): Promise<{
    success: boolean;
    data: {
      active_timers: any[];
    };
  }> => {
    try {
      const res = await caseAPI.getActiveTimers(caseId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch active timers');
    }
  },
  startTimer: async (caseId: number, payload?: { title?: string; notes?: string }): Promise<any> => {
    try {
      const res = await caseAPI.startTimer(caseId, payload);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to start timer');
    }
  },
  resumeTimer: async (caseId: number): Promise<any> => {
    try {
      const res = await caseAPI.resumeTimer(caseId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to resume timer');
    }
  },
  stopTimer: async (caseId: number, payload?: { deduction_seconds?: number }): Promise<any> => {
    try {
      const res = await caseAPI.stopTimer(caseId, payload);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to stop timer');
    }
  },
  finalizeTimer: async (caseId: number, payload: any): Promise<any> => {
    try {
      const res = await caseAPI.finalizeTimer(caseId, payload);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to finalize timer');
    }
  },
  resetTimer: async (caseId: number, payload?: any): Promise<any> => {
    try {
      const res = await caseAPI.resetTimer(caseId, payload);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to reset timer');
    }
  },
  updateTimeEntry: async (caseId: number, entryId: number, payload: any): Promise<any> => {
    try {
      const res = await caseAPI.updateTimeEntry(caseId, entryId, payload);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update time entry');
    }
  },
  deleteTimeEntry: async (caseId: number, entryId: number): Promise<any> => {
    try {
      const res = await caseAPI.deleteTimeEntry(caseId, entryId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to delete time entry');
    }
  },

  // Delete file from case
  deleteFile: async (caseId: number, fileId: number): Promise<any> => {
    try {
      const res = await caseAPI.deleteFile(caseId, fileId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to delete file');
    }
  },

  // Add comment to case
  addComment: async (caseId: number, data: any): Promise<any> => {
    try {
      const response = await caseAPI.addComment(caseId, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to add comment');
    }
  },

  // Update comment
  updateComment: async (caseId: number, commentId: number, data: any): Promise<any> => {
    try {
      const response = await caseAPI.updateComment(caseId, commentId, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update comment');
    }
  },

  // Delete comment
  deleteComment: async (caseId: number, commentId: number): Promise<any> => {
    try {
      const response = await caseAPI.deleteComment(caseId, commentId);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to delete comment');
    }
  },

  // New comment functionality
  updateCaseAndAddComment: async (caseId: number, data: any): Promise<any> => {
    try {
      const response = await caseAPI.updateCaseAndAddComment(caseId, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update case and add comment');
    }
  },

  getComments: async (caseId: number): Promise<any> => {
    try {
      const response = await caseAPI.getComments(caseId);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch comments');
    }
  },

  listFiles: async (caseId: number): Promise<any> => {
    try {
      const res = await caseAPI.listFiles(caseId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to list case files');
    }
  },
  downloadFile: async (caseId: number, fileId: number, fileName: string, onProgress?: (progress: number) => void): Promise<void> => {
    try {
      const res = await caseAPI.downloadFile(caseId, fileId, onProgress);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to download file');
    }
  },
  downloadFilesZip: async (caseId: number, fileIds: number[], zipName?: string, onProgress?: (progress: number) => void): Promise<Blob> => {
    try {
      const res = await caseAPI.downloadFilesZip(caseId, fileIds, zipName, onProgress);
      return res.data as Blob;
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to download zip');
    }
  },

  // Sync files from vendor case to client case
  syncFilesToClient: async (caseId: number): Promise<any> => {
    try {
      const res = await caseAPI.syncFilesToClient(caseId);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to sync files to client case');
    }
  },

  // Import files from SharePoint/OneDrive
  importFromSharePoint: async (caseId: number, sharepointUrl: string, options?: {
    isMrrDelivery?: boolean;
    isCaseDelivery?: boolean;
    deliveryComment?: string;
    tag?: string;
    isAdditionalUpload?: boolean;
  }): Promise<any> => {
    try {
      const payload: any = {
        sharepoint_url: sharepointUrl,
      };
      
      if (options?.isMrrDelivery) {
        payload.is_mrr_delivery = true;
      } else if (options?.isCaseDelivery) {
        payload.is_case_delivery = true;
        if (options.deliveryComment) {
          payload.delivery_comment = options.deliveryComment;
        }
      }
      
      if (options?.tag) {
        payload.tag = options.tag;
      }
      if (options?.isAdditionalUpload) {
        payload.is_additional_upload = true;
      }
      
      const res = await caseAPI.importFromSharePoint(caseId, payload);
      return res.data;
    } catch (error: any) {
      if (error.response?.data?.message) throw new Error(error.response.data.message);
      throw new Error('Failed to import files from SharePoint');
    }
  },
};
